import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { CoreService } from 'src/app/services/core.service';
import { AppSettings } from 'src/app/config';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { navItems } from './vertical/sidebar/sidebar-data';
import { getPaymentAccessMode, normalizeUserRoles } from 'src/app/core/utils/payment-access.util';
import { NavService } from '../../services/nav.service';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { RouterModule } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { TablerIconsModule } from 'angular-tabler-icons';
import { HeaderComponent } from './vertical/header/header.component';
import { AppHorizontalHeaderComponent } from './horizontal/header/header.component';
import { AppHorizontalSidebarComponent } from './horizontal/sidebar/sidebar.component';
import { AppBreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { AppVersionComponent } from './shared/app-version/app-version.component';
import { CustomizerComponent } from './shared/customizer/customizer.component';
import { BrandingComponent } from '../full/vertical/sidebar/branding.component';
import { SearchComponent } from 'src/app/core/components/search/search.component';
import { LoadSpinnerComponent } from 'src/app/load-spinner.component';
import { environment } from 'src/environments/environment';
import { HelperService } from 'src/app/core/services/helper.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { normalizeAppRoute, resolveFaqIdFromUrl } from 'src/app/core/faq/faq-route.resolver';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

// for mobile app sidebar
interface apps {
  id: number;
  icon: string;
  color: string;
  title: string;
  subtitle: string;
  link: string;
}

interface quicklinks {
  id: number;
  title: string;
  link: string;
}

@Component({
  selector: 'app-full',
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    TablerIconsModule,
    HeaderComponent,
    AppHorizontalHeaderComponent,
    AppHorizontalSidebarComponent,
    AppBreadcrumbComponent,
    AppVersionComponent,
    CustomizerComponent,
    BrandingComponent,
    SearchComponent,
    LoadSpinnerComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './full.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
  //changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FullComponent implements OnInit {
  authService = inject(AuthService);
  private readonly router = inject(Router);
  currentUser = this.authService.currentUser;
  navItems = navItems;

  searchOverlayActive: boolean = false;
  activeFaqId = signal(resolveFaqIdFromUrl(this.router.url));
  activeFaqRoute = signal(normalizeAppRoute(this.router.url));
  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  resView = false;
  selectedIcon = 1;

  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  //get options from service
  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;


  isFilterNavOpen: boolean = false;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  // for mobile app sidebar
  apps: apps[] = [
    {
      id: 1,
      icon: 'solar:chat-line-line-duotone',
      color: 'primary',
      title: 'Chat Application',
      subtitle: 'Messages & Emails',
      link: '/',
    },
    {
      id: 2,
      icon: 'solar:checklist-minimalistic-line-duotone',
      color: 'secondary',
      title: 'Todo App',
      subtitle: 'Completed task',
      link: '/',
    },
    {
      id: 3,
      icon: 'solar:bill-list-line-duotone',
      color: 'success',
      title: 'Invoice App',
      subtitle: 'Get latest invoice',
      link: '/',
    },
    {
      id: 4,
      icon: 'solar:calendar-line-duotone',
      color: 'error',
      title: 'Calendar App',
      subtitle: 'Get Dates',
      link: '/',
    },
    {
      id: 5,
      icon: 'solar:smartphone-2-line-duotone',
      color: 'warning',
      title: 'Contact Application',
      subtitle: '2 Unsaved Contacts',
      link: '/',
    },
    {
      id: 6,
      icon: 'solar:ticket-line-duotone',
      color: 'primary',
      title: 'Tickets App',
      subtitle: 'Create new ticket',
      link: '/',
    },
    {
      id: 7,
      icon: 'solar:letter-line-duotone',
      color: 'secondary',
      title: 'Email App',
      subtitle: 'Get new emails',
      link: '/',
    },
    {
      id: 8,
      icon: 'solar:book-2-line-duotone',
      color: 'warning',
      title: 'Contact List',
      subtitle: 'Create new contact',
      link: '/',
    },
  ];

  quicklinks: quicklinks[] = [
    {
      id: 1,
      title: 'Pricing Page',
      link: '/theme-pages/pricing',
    },
    {
      id: 2,
      title: 'Authentication Design',
      link: '/authentication/login',
    },
    {
      id: 3,
      title: 'Register Now',
      link: '/authentication/side-register',
    },
    {
      id: 4,
      title: '404 Error Page',
      link: '/authentication/error',
    },
    {
      id: 5,
      title: 'Notes App',
      link: '/apps/notes',
    },
    {
      id: 6,
      title: 'Employee App',
      link: '/apps/employee',
    },
    {
      id: 7,
      title: 'Todo Application',
      link: '/apps/todo',
    },
    {
      id: 8,
      title: 'Treeview',
      link: '/theme-pages/treeview',
    },
  ];

  onIconSelected(icon: number) {
    this.selectedIcon = icon;
  }

  constructor(
    private settings: CoreService,
    private mediaMatcher: MediaMatcher,
    private breakpointObserver: BreakpointObserver,
    private navService: NavService,
    public helperService: HelperService
  ) {
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[BELOWMONITOR];
        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    // This is for scroll to top
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nav = event as NavigationEnd;
        this.content.scrollTo({ top: 0 });
        this.activeFaqRoute.set(normalizeAppRoute(nav.urlAfterRedirects));
        this.activeFaqId.set(resolveFaqIdFromUrl(nav.urlAfterRedirects));
      });
  }

  ngOnInit(): void { }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
  }

  openClosedMenu() {
    this.options.sidenavCollapsed = false;
    this.resetCollapsedState();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.toggleDarkTheme(options);
    this.toggleColorsTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }

  toggleColorsTheme(options: AppSettings) {
    // Remove any existing theme class dynamically
    this.htmlElement.classList.forEach((className) => {
      if (className.endsWith('_theme')) {
        this.htmlElement.classList.remove(className);
      }
    });

    // Add the selected theme class
    this.htmlElement.classList.add(options.activeTheme);
  }

  searchOverlayActiveChange(event: any) {
    this.searchOverlayActive = event;
  }

  visibleNavItems = computed(() => {
    const userRoles = this.currentUser()?.group || [];
    return this.filterNavItems(navItems, userRoles);
  });

  private _isPcaMonitorVisible(): boolean {
    const env = environment as { pcaMonitorEnabled?: boolean; envName?: string };
    return env.pcaMonitorEnabled === true || env.envName === 'preprod' || env.envName === 'dev';
  }

  private filterNavItems(items: any[], userRoles: unknown): any[] {
    const accessMode = getPaymentAccessMode(userRoles);
    const normalizedRoles = normalizeUserRoles(userRoles);

    const filteredItems = items
      .filter(item => {
        if (item.pcaMonitorOnly && !this._isPcaMonitorVisible()) return false;
        if (!item.allowedRoles || item.allowedRoles.length === 0) return true;

        if (accessMode === 'notice-only') {
          return item.allowedRoles.includes('exec-payment') || item.allowedRoles.includes('exec_payment');
        }

        return item.allowedRoles.some((role: string) => {
          const normalizedRole = normalizeUserRoles([role])[0];
          return normalizedRoles.includes(normalizedRole);
        });
      })
      .map(item => ({
        ...item,
        children: item.children ? this.filterNavItems(item.children, userRoles) : []
      }));

    return this.removeOrphanNavCaps(filteredItems);
  }

  /** Hide section headers (navCap) when no route item remains in that section. */
  private removeOrphanNavCaps(items: any[]): any[] {
    const result: any[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.navCap) {
        let hasRouteBelow = false;
        for (let j = i + 1; j < items.length; j++) {
          if (items[j].navCap) {
            break;
          }
          if (items[j].route) {
            hasRouteBelow = true;
            break;
          }
        }
        if (!hasRouteBelow) {
          continue;
        }
      }
      result.push(item);
    }
    return result;
  }


}
